// src/components/ErrorBoundary.jsx
import React from 'react';
import Mascot from './Mascot';
import '../styles/components/StatePage.css';

// ─────────────────────────────────────────────────────────────────────────────
// What developers see:   Full error + stack trace, logged to console
// What users see:        Generic friendly message — no stack, no file paths
// What hackers see:      Nothing useful — no internal structure exposed
// ─────────────────────────────────────────────────────────────────────────────

const isDev = process.env.NODE_ENV === 'development';

// Safe user-facing messages — never expose real error text in production
const USER_MESSAGES = {
  default: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please refresh the page or try again later.',
  },
  network: {
    title: 'Connection problem',
    description: 'We couldn\'t reach the server. Check your connection and try again.',
  },
  auth: {
    title: 'Session expired',
    description: 'Your session has expired. Please log in again.',
  },
  notFound: {
    title: 'Page not found',
    description: 'The page or resource you\'re looking for doesn\'t exist.',
  },
};

function classifyError(error) {
  if (!error) return 'default';
  const msg = error.message?.toLowerCase() || '';
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch')) return 'network';
  if (msg.includes('401') || msg.includes('unauthorized') || msg.includes('token')) return 'auth';
  if (msg.includes('404') || msg.includes('not found')) return 'notFound';
  return 'default';
}

// ── Fallback UI ───────────────────────────────────────────────────────────────
function ErrorFallback({ error, errorInfo, onReset, context }) {
  const category = classifyError(error);
  const { title, description } = USER_MESSAGES[category];

  return (
    <div className="state-page" role="alert">
      <div className="state-page__inner">
        <div>
          <p className="state-page__display">Hold on.</p>
          <p className="state-page__code">SOMETHING BROKE ON OUR SIDE</p>
        </div>
        <div className="state-page__side">
          <div className="state-say">
            <Mascot size={40} />
            <div className="state-say__bubble">
              <b>Michii says</b>
              <span>Your guides are safe. Let’s try that again.</span>
            </div>
          </div>

          <h2 className="state-page__title">{title}</h2>

          {/* Description — always generic, never from the real error */}
          <p className="state-page__desc">{description}</p>

          <div className="state-page__actions">
            <button className="lp-btn" onClick={onReset || (() => window.location.reload())}>
              Try again
            </button>
            <button className="lp-tlink state-page__link" onClick={() => window.history.back()}>
              Go back
            </button>
          </div>

          {/* Context label (e.g. "WorkspaceDashboard") — only in dev */}
          {isDev && context && <p className="state-dev">Error in: {context}</p>}

          {/* Dev-only: expandable error details — never shown in production */}
          {isDev && error && (
            <details className="state-dev-details">
              <summary>[DEV] Error details</summary>
              <pre>
                {error.toString()}
                {errorInfo?.componentStack && `\n\nComponent Stack:${errorInfo.componentStack}`}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Error Boundary Class ──────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // ── Developer logging ──────────────────────────────────────────────────
    // Full details go to console (visible in browser devtools / server logs)
    // Never sent to the user or exposed in the DOM in production
    console.group(`[ErrorBoundary] ${this.props.context || 'Unknown'}`);
    console.error('Error:', error);
    console.error('Component stack:', errorInfo?.componentStack);
    console.groupEnd();

    // ── Optional: send to your error tracking service ──────────────────────
    // e.g. Sentry, Datadog, LogRocket — safe because it's server-side
    // if (typeof window.Sentry !== 'undefined') {
    //   window.Sentry.captureException(error, { extra: errorInfo });
    // }
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          context={this.props.context}
        />
      );
    }
    return this.props.children;
  }
}

// ── HOC for easy wrapping ─────────────────────────────────────────────────────
// Usage: export default withErrorBoundary(MyPage, 'MyPage');
export function withErrorBoundary(Component, context) {
  const displayName = context || Component.displayName || Component.name || 'Component';
  const Wrapped = (props) => (
    <ErrorBoundary context={displayName}>
      <Component {...props} />
    </ErrorBoundary>
  );
  Wrapped.displayName = `withErrorBoundary(${displayName})`;
  return Wrapped;
}

export default ErrorBoundary;
